import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  eatsKeys,
  imageApi,
  restaurantApi,
  uploadApi,
  IMAGE_ENTITY_TYPES,
  IMAGE_TYPES,
  UPLOAD_FOLDERS,
  type UpsertImageItem,
} from '@noor/eats-api';
import { toCreateInput, toUpdateInput } from '../lib/branch-form';
import type { BranchFormPhoto, BranchFormValues } from './schema';

/** Which photo slots held an image before this edit. */
export interface StoredPhotos {
  icon: boolean;
  background: boolean;
}

export interface UpsertBranchVars {
  values: BranchFormValues;
  /** Absent when creating. */
  branchId?: string | undefined;
  /**
   * What the branch's photo slots held when the drawer opened — the only way
   * to tell an untouched empty slot from one the merchant just cleared.
   * Defaults to "nothing stored", which is correct for a create.
   */
  stored?: StoredPhotos | undefined;
}

/**
 * What the write actually did.
 *
 * `pending` marks a write the backend QUEUED behind moderation instead of
 * applying — the list won't show the change until a moderator approves it.
 * `photosFailed` marks a branch that saved while its photos did not.
 *
 * `branchId` is the saved branch — the id the create MINTED, or the one the
 * update was addressed to. The wizard's step 2 attaches products to it, so a
 * create has to hand it back rather than leave the caller to re-read the list
 * and guess which row is new. It is ABSENT for a moderated create: that write
 * answers with a receipt and no restaurant, so no id exists yet.
 */
export interface UpsertBranchResult {
  status: 'applied' | 'pending';
  photosFailed: boolean;
  photoError?: unknown;
  branchId?: string;
}

/** Upload a freshly-picked photo and return its storage key. */
async function uploadPhoto(
  photo: BranchFormPhoto | undefined,
): Promise<string | undefined> {
  if (!photo?.file) return undefined;
  return uploadApi.upload(photo.file, UPLOAD_FOLDERS.restaurant);
}

/**
 * Create or update a branch, including its two photos.
 *
 * Photos are handled differently per mode, because the API is:
 *
 * - **Create** takes `icon` / `background_image` inline — a branch that doesn't
 *   exist yet has no id to address an image slot with.
 * - **Update** REJECTS those fields (`400 unknown field "icon"`), so a changed
 *   photo goes through the Image Service (`PUT/DELETE /images/restaurant/{id}`)
 *   after the PATCH lands.
 *
 * A photo upload failure does NOT abort the save. Object storage is a separate
 * host from the API and has failed on its own, and aborting would mean a photo
 * problem silently blocks edits to the name, address and schedule that never
 * involved a photo. The branch is saved without the new photos and the failure
 * is reported separately, so re-picking retries just that part.
 */
export function useUpsertBranch() {
  const queryClient = useQueryClient();

  return useMutation<UpsertBranchResult, Error, UpsertBranchVars>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ values, branchId, stored }) => {
      let iconKey: string | undefined;
      let backgroundKey: string | undefined;
      let photosFailed = false;
      let photoError: unknown;
      try {
        [iconKey, backgroundKey] = await Promise.all([
          uploadPhoto(values.icon),
          uploadPhoto(values.backgroundImage),
        ]);
      } catch (error) {
        photosFailed = true;
        photoError = error;
      }

      if (branchId === undefined) {
        const result = await restaurantApi.create(
          toCreateInput(values, iconKey, backgroundKey),
        );
        // The minted id, when the create actually applied. A gated merchant's
        // create answers with a `pending_change` and no restaurant, so this
        // stays absent rather than defaulting to an empty string a caller
        // might address a request with.
        const createdId = result.data.restaurant?.id;
        return {
          status: result.data.restaurant ? 'applied' : 'pending',
          photosFailed,
          ...(photoError !== undefined ? { photoError } : {}),
          ...(createdId ? { branchId: createdId } : {}),
        };
      }

      const result = await restaurantApi.update(branchId, toUpdateInput(values));

      // Photo slots are written after the profile PATCH, and only where the
      // merchant actually changed something: a freshly-picked photo sets its
      // slot, and a cleared one (a photo that was stored and is now gone from
      // the form) empties it. An untouched slot is left alone — a saved photo
      // carries no recoverable key, so re-sending it is impossible anyway.
      try {
        await syncPhotos(branchId, {
          icon: { key: iconKey, photo: values.icon, hadPhoto: stored?.icon },
          background: {
            key: backgroundKey,
            photo: values.backgroundImage,
            hadPhoto: stored?.background,
          },
        });
      } catch (error) {
        photosFailed = true;
        photoError = error;
      }

      return {
        status: result.data.restaurant ? 'applied' : 'pending',
        photosFailed,
        ...(photoError !== undefined ? { photoError } : {}),
        branchId,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eatsKeys.restaurants.all });
    },
  });
}

/** What the form says about one photo slot, versus what was stored before. */
interface SlotState {
  /** Set when the merchant picked a new photo and it uploaded. */
  key: string | undefined;
  /** The slot's current form value — absent means the merchant cleared it. */
  photo: BranchFormPhoto | undefined;
  /** Whether the slot held a photo when the drawer opened. */
  hadPhoto: boolean | undefined;
}

/**
 * Bring both image slots in line with the form: set each to its newly uploaded
 * key, or empty it when the merchant removed the photo that was there.
 *
 * The two SETS go as ONE batched upsert — a branch's icon and background are
 * the canonical batch the Image Service was shaped for, and sending them
 * together means a save can no longer land one photo and fail the other.
 * Clears stay separate: a delete addresses one selector by definition.
 *
 * Does nothing to a slot that is untouched — including the common case of one
 * that was empty and stayed empty, which must NOT issue a delete: a branch
 * created without a logo would otherwise fire a pointless `DELETE .../icon` on
 * every subsequent save.
 */
async function syncPhotos(
  branchId: string,
  slots: { icon: SlotState; background: SlotState },
): Promise<void> {
  const items: UpsertImageItem[] = [];
  if (slots.icon.key) {
    items.push({ image_type: IMAGE_TYPES.icon, link: slots.icon.key });
  }
  if (slots.background.key) {
    items.push({ image_type: IMAGE_TYPES.background, link: slots.background.key });
  }
  if (items.length > 0) {
    await imageApi.upsert(IMAGE_ENTITY_TYPES.restaurant, branchId, items);
  }

  // Gone from the form but stored before — the merchant cleared it. A slot that
  // just received a new photo is never also cleared, hence the `!key` guard.
  const cleared = (
    [
      [slots.icon, IMAGE_TYPES.icon],
      [slots.background, IMAGE_TYPES.background],
    ] as const
  ).filter(([slot]) => !slot.key && !slot.photo && slot.hadPhoto);

  await Promise.all(
    cleared.map(([, imageType]) =>
      imageApi.remove(IMAGE_ENTITY_TYPES.restaurant, branchId, imageType),
    ),
  );
}
